import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { LinkPopupIcon } from "@/icons";
import { linkPopupStateAtom } from "@/interface/AppStore";
import { HStack } from "@/shared/ui/Stack";
import { showSnackbar } from "@/snackbar/snackbar";
import styles from "./LinkPopup.module.css";
import { calculatePopupCoordinates } from "./utils/calculatePopupCoordinates";
import { getLinkForView } from "./utils/getLinkForView";

export function LinkPopup() {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const state = useAtomValue(linkPopupStateAtom);

    useEffect(() => {
        if (!state || !wrapperRef.current) {
            return;
        }

        const wrapper = wrapperRef.current;
        const wrapperRect = wrapper.getBoundingClientRect();
        const canvasRect = canv.canvas.getBoundingClientRect();
        const { left, top } = calculatePopupCoordinates(state, wrapperRect, canvasRect);

        wrapper.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    }, [state]);

    if (!state) {
        return null;
    }

    const controller = canv.appInterface.linkPopupController;
    const url = state.url;

    const handlePointerDown = (event: React.PointerEvent) => {
        event.preventDefault();
    };

    const handleOpenLinkButtonClick = () => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCopyLinkButtonClick = async () => {
        try {
            await navigator.clipboard.writeText(url);
            showSnackbar(t("textLink.copied"), { type: "success", duration: SNACKBAR_TIME });
        } catch {
            console.warn("Link copy failed", { type: "error", duration: SNACKBAR_TIME });
        }
    };

    const viewLink = getLinkForView(state.url);

    return (
        <HStack
            ref={wrapperRef}
            className={styles.wrapper}
            onPointerEnter={controller.onPopupPointerEnter}
            onPointerLeave={controller.onPopupPointerLeave}
            onPointerDown={handlePointerDown}
            data-ui-root="true"
        >
            <div className={styles.arrow} />
            <HStack className={styles.content}>
                <button className={styles.link} type="button" onClick={handleOpenLinkButtonClick}>
                    <LinkPopupIcon />

                    <span className={styles.linkText}>
                        {t("textLink.open", { link: viewLink })}
                    </span>
                </button>

                <div className={styles.divider} />

                <button className={styles.button} type="button" onClick={handleCopyLinkButtonClick}>
                    {t("textLink.copy")}
                </button>
            </HStack>
        </HStack>
    );
}
